// Scorecard photos are stored as base64 inside the game document, which
// Firestore caps at 1 MiB. Keep a margin for the rest of the game data.
export const SCORECARD_PHOTO_LIMITS = {
  mobile: { maxWidth: 960, maxHeight: 540 },
  desktop: { maxWidth: 1920, maxHeight: 1080 }
};

const MAX_INPUT_BYTES = 25 * 1024 * 1024;
const MAX_DATA_URL_LENGTH = 850000;
const DEFAULT_TIMEOUT_MS = 15000;
const QUALITY_FALLBACKS = [0.65, 0.5];

const timeoutError = () => new Error('Image took too long to load. Please try again.');
const decodeError = () =>
  new Error('Failed to load image. The file may be corrupted.');

const decodeWithBitmap = (file, timeoutMs) =>
  new Promise((resolve, reject) => {
    if (typeof window.createImageBitmap !== 'function') {
      reject(new Error('Image decoding is not available.'));
      return;
    }

    let settled = false;
    let timer = null;

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      callback(value);
    };

    timer = setTimeout(() => finish(reject, timeoutError()), timeoutMs);

    window
      .createImageBitmap(file)
      .then((bitmap) => {
        if (settled) {
          if (typeof bitmap?.close === 'function') bitmap.close();
          return;
        }
        finish(resolve, bitmap);
      })
      .catch(() => finish(reject, decodeError()));
  });

const decodeWithImage = (objectUrl, timeoutMs) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;
    let timer = null;

    const clearHandlers = () => {
      if (timer) clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
    };

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearHandlers();
      callback(value);
    };

    image.decoding = 'async';
    image.onload = () => finish(resolve, image);
    image.onerror = () => finish(reject, decodeError());
    timer = setTimeout(() => finish(reject, timeoutError()), timeoutMs);
    image.src = objectUrl;
  });

const decodeSource = async (file, objectUrl, timeoutMs) => {
  try {
    const bitmap = await decodeWithBitmap(file, timeoutMs);
    if (bitmap?.width && bitmap?.height) {
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => {
          if (typeof bitmap.close === 'function') bitmap.close();
        }
      };
    }
    if (typeof bitmap?.close === 'function') bitmap.close();
  } catch {
    // Safari/iOS versions with partial createImageBitmap support land here.
    // The <img> path below is the required fallback, not an optional one.
  }

  const image = await decodeWithImage(objectUrl, timeoutMs);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (!width || !height) {
    image.src = '';
    throw decodeError();
  }

  return {
    source: image,
    width,
    height,
    cleanup: () => {
      image.onload = null;
      image.onerror = null;
      image.src = '';
    }
  };
};

const fitDimensions = (width, height, maxWidth, maxHeight) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
};

const encodeWithinBudget = (canvas, source, target, qualities, maxDataUrlLength) => {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Failed to process the photo.');

  const encode = (width, height, quality) => {
    canvas.width = width;
    canvas.height = height;
    context.imageSmoothingQuality = 'high';
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (!dataUrl.startsWith('data:image/jpeg')) {
      throw new Error('Failed to process the photo.');
    }
    return dataUrl;
  };

  for (let index = 0; index < qualities.length; index += 1) {
    const dataUrl = encode(target.width, target.height, qualities[index]);
    if (dataUrl.length <= maxDataUrlLength) return dataUrl;
  }

  const reduced = {
    width: Math.max(1, Math.round(target.width * 0.8)),
    height: Math.max(1, Math.round(target.height * 0.8))
  };

  for (let index = 0; index < qualities.length; index += 1) {
    const dataUrl = encode(reduced.width, reduced.height, qualities[index]);
    if (dataUrl.length <= maxDataUrlLength) return dataUrl;
  }

  throw new Error('That photo is too large to save. Try a closer photo.');
};

export const resizeImageFile = async (
  file,
  {
    maxWidth,
    maxHeight,
    quality = 0.8,
    maxInputBytes = MAX_INPUT_BYTES,
    maxDataUrlLength = MAX_DATA_URL_LENGTH,
    timeoutMs = DEFAULT_TIMEOUT_MS
  } = {}
) => {
  if (!file || !file.size) throw new Error('The selected photo is empty.');
  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('Please select a valid image file.');
  }
  if (file.size > maxInputBytes) {
    throw new Error('That photo is too large. Choose a photo under 25 MB.');
  }
  if (!maxWidth || !maxHeight) {
    throw new Error('The photo size is not configured.');
  }

  const objectUrl = URL.createObjectURL(file);
  let decoded = null;
  let canvas = null;

  try {
    decoded = await decodeSource(file, objectUrl, timeoutMs);
    const target = fitDimensions(decoded.width, decoded.height, maxWidth, maxHeight);
    canvas = document.createElement('canvas');
    const qualities = [quality, ...QUALITY_FALLBACKS].filter(
      (value, index, values) => value > 0 && values.indexOf(value) === index
    );

    return encodeWithinBudget(canvas, decoded.source, target, qualities, maxDataUrlLength);
  } finally {
    if (decoded) decoded.cleanup();
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
    URL.revokeObjectURL(objectUrl);
  }
};

export const prepareScorecardPhoto = (file, { isMobile = false, timeoutMs } = {}) => {
  const limits = isMobile ? SCORECARD_PHOTO_LIMITS.mobile : SCORECARD_PHOTO_LIMITS.desktop;
  return resizeImageFile(file, {
    ...limits,
    quality: 0.8,
    ...(timeoutMs ? { timeoutMs } : {})
  });
};
